// Lead search filters, shared by the CRM (the editable chips) and the lead_search edge function, which
// validates them again before spending a look-up. Pure apart from zod.
import { z } from "zod";

export const LEAD_ENGINES = [
  "apollo_people",
  "apollo_companies",
  "apify_maps",
] as const;
export type LeadEngine = (typeof LEAD_ENGINES)[number];
/** The services whose API keys an admin pastes in Settings */
export const KEY_ENGINES = ["apollo", "apify"] as const;
export type KeyEngine = (typeof KEY_ENGINES)[number];
/** The key and daily cap each search engine uses */
export const ENGINE_KEY: Record<LeadEngine, KeyEngine> = {
  apollo_people: "apollo",
  apollo_companies: "apollo",
  apify_maps: "apify",
};

export const EMPLOYEE_RANGES = [
  "1,10",
  "11,50",
  "51,200",
  "201,500",
  "501,1000",
  "1001,5000",
  "5001,10000",
] as const;

const chip = z.string().trim().min(1).max(60);
export const LeadFiltersSchema = z.object({
  keywords: z.array(chip).max(5).default([]),
  job_titles: z.array(chip).max(5).default([]),
  locations: z.array(chip).max(3).default([]),
  employee_ranges: z.array(z.enum(EMPLOYEE_RANGES)).max(7).default([]),
});
export type LeadFilters = z.infer<typeof LeadFiltersSchema>;

/** A search result, before the user ticks it */
export type Lead = {
  ref: string;
  /** the engine that found it; "wikidata" needs no key (openData.ts) */
  source: KeyEngine | "wikidata";
  company: {
    name: string;
    website?: string;
    domain?: string;
    phone?: string;
    address?: string;
    city?: string;
    country?: string;
    linkedin_url?: string;
    industry?: string;
    employees?: number;
  };
  contact?: {
    first_name: string;
    last_name?: string;
    title?: string;
    email?: string;
    linkedin_url?: string;
  };
};

/** "https://www.Acme.com/x" -> "acme.com"; undefined when it is not a web address */
export const domainOf = (url?: string | null) => {
  if (!url) return undefined;
  try {
    const host = new URL(
      /^https?:\/\//i.test(url) ? url : `https://${url}`,
    ).hostname
      .toLowerCase()
      .replace(/^www\./, "");
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) ? host : undefined;
  } catch {
    return undefined;
  }
};
